import { useEffect, useRef, useState } from 'react';
import { type HeroVideoSourceType } from '@/util/util_heroVideo';
import { youTubeBackgroundEmbedUrl } from '@/util/util_youtube';

// The hero video plays silent, looping and cropped to fill the section, behind
// the title. Two players, because the two sources behave differently: an
// uploaded file is a `<video>` tag we control completely, a YouTube link is an
// iframe that paints its own title bar and play button over the first frame
// until playback begins. Both are covered by the hero still until the video is
// actually moving, so the visitor only ever sees the photo or the video —
// never YouTube's chrome, never a black box.

// An iframe can't be object-cover'd, so the YouTube player is sized to the
// larger of the two 16:9 boxes that cover this section and centred, letting the
// overflow crop it. The 124.45vh is 70vh × 16/9 — it is tied to the hero
// section's h-[70vh] in PageExperienceDetail.
const YOUTUBE_COVER_STYLE = { width: 'max(100%, 124.45vh)', height: 'max(100%, 56.25vw)' };

// How long to hold the poster if the YouTube player never reports playback —
// autoplay refused, or an extension eating the message channel. A video that is
// quietly still loading looks better than a poster that never leaves.
const POSTER_FALLBACK_MS = 4000;

// The player answers only once we announce ourselves on its channel, and drops
// the greeting if it lands before the widget inside the iframe is listening —
// hence repeating it rather than sending once on load.
const HANDSHAKE_INTERVAL_MS = 250;

const PLAYER_ORIGIN = 'https://www.youtube-nocookie.com';

// YouTube's player states; 1 is PLAYING.
const PLAYING = 1;

/** Reads the player state out of a message from the embed, if it carries one. */
function playerStateOf(data: unknown): number | undefined {
  let payload: unknown = data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return undefined;
    }
  }
  if (typeof payload !== 'object' || payload === null) return undefined;

  const { event, info } = payload as { event?: unknown; info?: unknown };
  // onStateChange carries the state directly; infoDelivery wraps it in `info`.
  if (event === 'onStateChange' && typeof info === 'number') return info;
  if (typeof info === 'object' && info !== null) {
    const { playerState } = info as { playerState?: unknown };
    if (typeof playerState === 'number') return playerState;
  }
  return undefined;
}

interface HeroVideoProps {
  source: HeroVideoSourceType;
  /** Shown while the video loads, and if it never starts. */
  posterImage: string;
  /** Experience name, for the iframe title and the poster's alt text. */
  name: string;
}

/** Full-bleed background video for the experience hero. */
export function HeroVideo({ source, posterImage, name }: HeroVideoProps) {
  return (
    <div className='tw:pointer-events-none tw:absolute tw:inset-0 tw:overflow-hidden'>
      {source.kind === 'file' ? (
        // `poster` covers the load natively: the still is painted until the
        // first frame is decoded, with nothing to fade and no JS involved.
        <video
          src={source.url}
          poster={posterImage || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload='auto'
          aria-hidden
          tabIndex={-1}
          className='tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-cover'
        />
      ) : (
        <YouTubeHeroVideo videoId={source.videoId} posterImage={posterImage} name={name} />
      )}
    </div>
  );
}

/** The YouTube half: an embed, with the poster held over it until it plays. */
function YouTubeHeroVideo({ videoId, posterImage, name }: { videoId: string } & Omit<HeroVideoProps, 'source'>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    // Nothing left to listen for once the poster is down, and the handshake is
    // not free to leave running: each `listening` re-registers us with the
    // widget, so YouTube answers every one of them with an infoDelivery message
    // that this effect then parses — four round trips a second for as long as
    // the page stays open.
    if (playing) return;

    const handshake = window.setInterval(() => {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening', id: videoId, channel: 'widget' }),
        PLAYER_ORIGIN,
      );
    }, HANDSHAKE_INTERVAL_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== PLAYER_ORIGIN) return;
      if (playerStateOf(event.data) === PLAYING) setPlaying(true);
    };
    window.addEventListener('message', onMessage);

    const fallback = window.setTimeout(() => setPlaying(true), POSTER_FALLBACK_MS);

    return () => {
      window.clearInterval(handshake);
      window.clearTimeout(fallback);
      window.removeEventListener('message', onMessage);
    };
  }, [videoId, playing]);

  return (
    <>
      <iframe
        ref={iframeRef}
        src={youTubeBackgroundEmbedUrl(videoId)}
        title={`${name} video`}
        allow='autoplay; encrypted-media; picture-in-picture'
        referrerPolicy='strict-origin-when-cross-origin'
        aria-hidden
        tabIndex={-1}
        style={YOUTUBE_COVER_STYLE}
        className='tw:absolute tw:left-1/2 tw:top-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:border-0'
      />
      {/* Only when there is one: `src=""` on an experience with no hero image
          paints a broken-image glyph and the alt text across the whole hero
          until the fade, which is worse than the player's own first frame. */}
      {posterImage && (
        <img
          src={posterImage}
          alt={name}
          aria-hidden={playing}
          className={`tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-cover tw:transition-opacity tw:duration-700 ${
            playing ? 'tw:opacity-0' : 'tw:opacity-100'
          }`}
        />
      )}
    </>
  );
}
